/**
 * "500+250+120" -> 870.
 *
 * Counting stock means counting several part-full containers of the same thing,
 * so the quantity box accepts a sum rather than making somebody do the addition
 * in their head next to a hot fryer. This is the best thing about the screen and
 * it is worth keeping exactly as it is.
 *
 * What changed is the reporting. The old version surfaced whatever the
 * JavaScript engine threw, so a missing operator told the user "Unexpected
 * number" and an unclosed bracket said "Unexpected token '}'" - naming a brace
 * they never typed and cannot see. It also went red the instant a '+' was typed,
 * punishing the one feature worth using, because a trailing operator is
 * indistinguishable from a syntax error if you only ask "does this evaluate".
 *
 * So there are three outcomes here, not two: a finished number, a sum still
 * being typed, and something actually wrong.
 */

export const STATE = {
    EMPTY: 'empty',
    OK: 'ok',
    INCOMPLETE: 'incomplete',   // mid-typing, e.g. "500+" - not an error
    INVALID: 'invalid',
};

const ALLOWED = /^[0-9+\-*/().\s]*$/;
const ENDS_OPEN = /[+\-*/(]\s*$/;

/**
 * Read what has been typed.
 *
 * Returns { state, value, message }. `value` is a finite number only when the
 * state is OK. Nothing here throws: the caller renders a state, and a half-typed
 * sum is a state rather than an exception.
 */
export function read(text) {
    const source = String(text ?? '').trim();
    if (source === '') return { state: STATE.EMPTY, value: null, message: '' };

    if (!ALLOWED.test(source)) {
        return {
            state: STATE.INVALID,
            value: null,
            message: 'Use numbers and + only',
        };
    }
    if (ENDS_OPEN.test(source)) {
        // Still being typed. Show the running total of the part before the
        // operator rather than an error.
        return { state: STATE.INCOMPLETE, value: partial(source), message: '' };
    }

    let result;
    try {
        // eslint-disable-next-line no-new-func
        result = Function(`"use strict"; return (${source});`)();
    } catch (error) {
        return {
            state: STATE.INVALID,
            value: null,
            message: 'That is not a number or a sum',
        };
    }

    if (typeof result !== 'number' || !Number.isFinite(result)) {
        return {
            state: STATE.INVALID,
            value: null,
            message: 'That is not a number or a sum',
        };
    }
    if (result < 0) {
        return {
            state: STATE.INVALID,
            value: null,
            message: 'Cannot be less than zero',
        };
    }
    return { state: STATE.OK, value: result, message: '' };
}

/** The total of the complete part of a sum still being typed. */
function partial(source) {
    const trimmed = source.replace(ENDS_OPEN, '').trim();
    if (trimmed === '') return null;
    const attempt = read(trimmed);
    return attempt.state === STATE.OK ? attempt.value : null;
}

/** The number to send, or null. Stock is whole grams and pieces. */
export function toQuantity(text) {
    const { state, value } = read(text);
    if (state !== STATE.OK) return null;
    return Math.round(value);
}

/** 3600 -> "3,600", the way the rest of the app writes figures. */
export function groupDigits(value) {
    if (value === null || value === undefined || value === '') return '';
    return Number(value).toLocaleString('en-IN');
}

/**
 * Is this figure wildly unlike what was there before?
 *
 * A stock count feeds the margin calculation, and this codebase has already
 * proved bad numbers reach the database - 17% of bill quantity lines were
 * physically impossible, including 21 tonnes of onion. An extra zero is the
 * cheapest mistake to make and the most expensive to find later.
 *
 * This warns and never blocks. Stock legitimately jumps when a delivery lands,
 * and a screen that argues with someone holding the actual sack is a screen
 * they will learn to click through.
 */
export function looksWrong(value, previous) {
    if (value === null || value === undefined) return null;
    const before = Number(previous);
    if (!Number.isFinite(before) || before <= 0) return null;
    if (value >= before * 10) return { kind: 'high', previous: before };
    if (value > 0 && value <= before / 10) return { kind: 'low', previous: before };
    return null;
}

/**
 * Figures the analytics layer would throw away as physically impossible.
 *
 * These are the server's own bounds, from store_management/expenses.py, and
 * they are the one thing on this screen that does block. A soft warning is
 * right for "that is a big jump"; it is not right for 200 kg of coriander,
 * because this field sets stock absolutely and the wrong number stays wrong.
 */
const IMPOSSIBLE = {
    g: { min: 5, max: 200000, label: 'g' },              // 5 g to 200 kg
    ml: { min: 5, max: 200000, label: 'ml' },
    p: { min: 1, max: 5000, label: 'pieces' },
};

const UNIT_ALIASES = {
    g: 'g', grams: 'g', gram: 'g', kg: 'g', kilograms: 'g',
    ml: 'ml', millilitres: 'ml', milliliters: 'ml', l: 'ml', litres: 'ml',
    p: 'p', piece: 'p', pieces: 'p', peices: 'p',
};

/**
 * The units a person may count an item in, and what one of each is worth in the
 * unit the item is actually stored in.
 *
 * The point is that nobody types a decimal point. Counting 3 kg of onion is
 * "3" then the kg key - not "3000", and not "3.0". Small items stay in grams
 * where they read naturally: 220 g rather than 0.22 kg.
 */
export function unitChoices(storedUnit) {
    const unit = String(storedUnit || '').toLowerCase();
    if (unit === 'g' || unit === 'grams' || unit === 'gram') {
        return [
            { label: 'g', factor: 1 },
            { label: 'kg', factor: 1000 },
        ];
    }
    if (unit === 'ml' || unit === 'millilitres' || unit === 'milliliters') {
        return [
            { label: 'ml', factor: 1 },
            { label: 'L', factor: 1000 },
        ];
    }
    // Pieces have no larger cousin, and neither does anything unrecognised.
    return [{ label: storedUnit, factor: 1 }];
}

export function isImpossible(value, unit) {
    if (value === null || value === undefined) return null;
    const bounds = IMPOSSIBLE[UNIT_ALIASES[String(unit || '').toLowerCase()]];
    if (!bounds) return null;
    // Zero is a real answer - it means the shelf is empty - so only a positive
    // figure below the floor is suspect.
    if (value > 0 && value < bounds.min) {
        return `Less than ${bounds.min} ${bounds.label} - check the unit`;
    }
    if (value > bounds.max) {
        return `More than ${groupDigits(bounds.max)} ${bounds.label} - check for an extra zero`;
    }
    return null;
}
